import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual } from 'react-redux';

import { Box, Button, Icon, Input, Label, Text, VisuallyHidden } from '@zillow/constellation-10';
import { IconErrorFilled } from '@zillow/constellation-icons';

import {
  DatarouterError,
  stringHelper,
  useRentalManagerUserEmailVerificationWebApiHandler_sendVerificationCode,
} from '@zg-rentals/rental-manager-env';
import { logError } from '@zg-rentals/log-error';

import { useSelector } from '../../../../../selectors/useSelector';

import numberUtils from '../../../../../utils/numberUtils';

import { MODES, VERIFICATION_STEPS } from '../../constants';
import StringConstants from '../../../../../constants/StringConstants';

import VerificationBodyTemplate from '../VerificationBodyTemplate';
import MaxAttemptsError from '../MaxAttemptsError';

import type { ChangeEvent, KeyboardEvent } from 'react';
import type { MODES_VALUES } from '../../constants';
import type { ErrorRMWProps } from '../../../../../model/ErrorRMW';

const CONFIG = {
  phone: {
    heading: 'Add your phone number',
    subheading:
      'To help protect your identity, Zillow will send a verification code to this number. You can choose whether to show the number on your listing.',
    ctaLabel: 'Your phone number:',
    placeholder: '(123) 456-7890',
    type: 'tel',
    value: (val: string) => numberUtils.formatPhoneNumberInput(val),
    ariaText: 'example-telephone',
    submitButtonLabel: 'Next',
  },
  email: {
    heading: 'Add your email address and get verification code',
    subheading: 'To help protect your identity, Zillow will send a verification code to this email.',
    ctaLabel: 'Your email address:',
    placeholder: 'example@example.com',
    type: 'email',
    value: (val: string) => val,
    ariaText: 'example-email',
    submitButtonLabel: 'Send verification code',
  },
};

type Props = {
  currentStepIndex: number;
  incrementStep: (step?: string) => void;
  onContentValueAdded?: (inputValue: string) => Promise<void>;
  saveContentToVerify: (inputValue: string) => void;
  verificationMode?: MODES_VALUES;
};

const AddPhoneNumberOrEmail = ({
  currentStepIndex,
  incrementStep,
  onContentValueAdded,
  saveContentToVerify,
  verificationMode = MODES.PHONE,
}: Props) => {
  const { contactEmail, contactPhone, listingAlias, preferredContactEmail, userPhoneNumber } = useSelector(
    (state) => ({
      contactEmail: state.properties.currentProperty.contactEmail,
      contactPhone: state.properties.currentProperty.contactPhone,
      listingAlias: state.properties.currentProperty.listingAlias,
      preferredContactEmail: state.properties.currentProperty.preferredContactEmail,
      userPhoneNumber: state.user.phoneNumber,
    }),
    shallowEqual,
  );

  const initialInputValue = useMemo(() => {
    switch (verificationMode) {
      case MODES.PHONE:
        return contactPhone || userPhoneNumber;
      case MODES.EMAIL:
        return contactEmail || preferredContactEmail;
      default:
        return '';
    }
  }, []);

  const [inputValue, setInputValue] = useState(initialInputValue || '');
  const [error, setError] = useState<Partial<ErrorRMWProps>>({});
  const [isLoading, setIsLoading] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const { mutate: sendVerificationCode } = useRentalManagerUserEmailVerificationWebApiHandler_sendVerificationCode();

  const isButtonDisabled = error?.code === StringConstants.SS_ERRORS.attempt_limit_reached;

  useEffect(() => {
    inputRef?.current?.select();
  }, []);

  const handlePhoneNextClick = useCallback((phone: string) => {
    const cleanPhoneNumber = numberUtils.cleanPhoneNumber(phone);

    if (cleanPhoneNumber.length < 10) {
      setError({ message: StringConstants.SS_ERRORS.invalid_phone_number });
      inputRef?.current?.focus();

      return;
    }

    if (cleanPhoneNumber === contactPhone) {
      incrementStep(VERIFICATION_STEPS.VERIFICATION_SUCCESS);

      return;
    }

    if (onContentValueAdded) {
      setIsLoading(true);

      onContentValueAdded(cleanPhoneNumber)
        .then(() => incrementStep(VERIFICATION_STEPS.VERIFICATION_SUCCESS))
        .catch(({ code, message }: ErrorRMWProps) => {
          if (code === StringConstants.SS_ERRORS.confirm_phone) {
            saveContentToVerify(cleanPhoneNumber);
            incrementStep();

            return;
          }

          setError({ code, message });
          inputRef?.current?.focus();
        })
        .finally(() => setIsLoading(false));
    }
  }, []);

  const handleEmailNextClick = useCallback((email: string) => {
    if (!stringHelper.validateEmail(email)) {
      setError({ message: StringConstants.SS_ERRORS.invalid_email_address });
      inputRef?.current?.focus();

      return;
    }

    if (email === contactEmail) {
      incrementStep(VERIFICATION_STEPS.VERIFICATION_SUCCESS);

      return;
    }

    if (onContentValueAdded) {
      setIsLoading(true);

      onContentValueAdded(email)
        .then(() => incrementStep(VERIFICATION_STEPS.VERIFICATION_SUCCESS))
        .catch((err: ErrorRMWProps) => {
          if (
            err?.code === StringConstants.SS_ERRORS.email_verification_pending ||
            err?.code === StringConstants.SS_ERRORS.email_verification_required
          ) {
            saveContentToVerify(email);
            sendVerificationCode(
              { getParams: { listingAlias, email: email } },
              {
                onSuccess: () => incrementStep(),
                onError: (err) => {
                  const errorCode = DatarouterError.getDisplayCode(err);
                  const errorMessage = DatarouterError.getDisplayMessage(err);

                  if (errorMessage) {
                    setError({
                      code: errorCode,
                      message: errorMessage,
                    });
                    logError({
                      error: err,
                      context: 'requestVerificationCode',
                    });

                    inputRef?.current?.focus();
                    setIsLoading(false);
                  }
                },
              },
            );

            return;
          }

          setError({ code: err?.code, message: err?.message || '' });
          inputRef?.current?.focus();
          setIsLoading(false);
        });
    }
  }, []);

  const handleNextClick = () => {
    switch (verificationMode) {
      case MODES.PHONE:
        handlePhoneNextClick(inputValue);

        break;
      case MODES.EMAIL:
        handleEmailNextClick(inputValue);

        break;
      default:
        return;
    }
  };

  const onChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    setError({});
    setInputValue(e.target.value);
  };

  const onKeyDownHandler = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleNextClick();
    }
  };

  const renderErrorMessage = () => {
    if (isButtonDisabled) {
      return <MaxAttemptsError verificationMode={verificationMode} />;
    }

    return (
      <Box css={{ marginTop: '400' }}>
        <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon
            size="md"
            css={{ color: 'icon.feedback.critical.hero', marginRight: '200' }}
            render={<IconErrorFilled />}
            title="Error"
          />
          <Text
            id={`${verificationMode}-error`}
            css={{ color: 'text.feedback.critical.strong', lineHeight: '18px', textAlign: 'center' }}
            asChild
          >
            <p>{`${error?.message}`}</p>
          </Text>
        </Box>
      </Box>
    );
  };

  return (
    <VerificationBodyTemplate
      heading={`Step ${currentStepIndex + 1} - ${CONFIG[verificationMode].heading}`}
      subheading={CONFIG[verificationMode].subheading}
    >
      <Box
        css={{
          marginBlock: '400',
          marginInline: 'auto',
          width: verificationMode === MODES.EMAIL ? '250px' : '200px',
          lg: {
            marginBlock: '800',
          },
        }}
      >
        <Label
          size="lg"
          htmlFor={`${verificationMode}Input`}
          css={{ display: 'block', marginBottom: { base: '200', lg: '400' } }}
        >
          {CONFIG[verificationMode].ctaLabel}
        </Label>
        <Box css={{ marginBottom: '200' }}>
          <Input
            id={`${verificationMode}Input`}
            data-testid={`${verificationMode}Input`}
            className="fs-mask"
            error={!!error?.message}
            onChange={onChangeHandler}
            onKeyDown={onKeyDownHandler}
            placeholder={CONFIG[verificationMode].placeholder}
            ref={inputRef}
            type={CONFIG[verificationMode].type}
            value={CONFIG[verificationMode].value(inputValue)}
            aria-describedby={error?.message ? `${verificationMode}-error` : CONFIG[verificationMode].ariaText}
          />
          <VisuallyHidden>
            <Text id={CONFIG[verificationMode].ariaText} css={{ paddingTop: '200' }} asChild>
              <p>Example: {CONFIG[verificationMode].placeholder}</p>
            </Text>
          </VisuallyHidden>
        </Box>
      </Box>

      <Button
        data-testid={`${verificationMode}NextButton`}
        tone="brand"
        emphasis="filled"
        disabled={isButtonDisabled}
        loading={isLoading}
        onClick={handleNextClick}
        size="md"
        css={{
          minWidth: '200px',
          display: 'block',
          margin: '0 auto',
        }}
      >
        {CONFIG[verificationMode].submitButtonLabel}
      </Button>

      {Boolean(error?.message) && (
        <Box css={{ marginBlock: '200' }} aria-live="assertive">
          {renderErrorMessage()}
        </Box>
      )}
    </VerificationBodyTemplate>
  );
};

export default AddPhoneNumberOrEmail;
